import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  StoreDetailsFeatureFlagClient,
  StoreDetailsFeatureFlagPendingAssignment,
  StoreDetailsFeatureFlagRow,
  StoreDetailsFeatureFlagsTabActions,
  StoreDetailsFeatureFlagsTabModel,
} from '../../../../types/features/storeDetails/featureFlags';
import {
  mapStoreDetailsFeatureFlagClientToApiClient,
  mapStoreDetailsFeatureFlagsResponseToRows,
} from '../mappers/storeDetailsFeatureFlagRowMapper';
import storeDetailsFeatureFlagsService from '../services/storeDetailsFeatureFlagsService';
import {
  buildStoreDetailsFeatureFlagClientSearchParams,
  getStoreDetailsFeatureFlagClientFromSearchParams,
} from '../utils/storeDetailsFeatureFlagsQueryParams';
import showToast from '../../../../utils/toastUtils';

export const useStoreDetailsFeatureFlags = (
  storeId: string,
): StoreDetailsFeatureFlagsTabModel & StoreDetailsFeatureFlagsTabActions => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeClient, setActiveClient] = useState<StoreDetailsFeatureFlagClient>(
    () => getStoreDetailsFeatureFlagClientFromSearchParams(searchParams),
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mutatingFeatureId, setMutatingFeatureId] = useState('');
  const [mutationError, setMutationError] = useState('');
  const [pendingAssignment, setPendingAssignment] = useState<StoreDetailsFeatureFlagPendingAssignment | null>(null);
  const [rows, setRows] = useState<StoreDetailsFeatureFlagRow[]>([]);
  const requestSequenceRef = useRef(0);

  useEffect(() => {
    const clientFromUrl = getStoreDetailsFeatureFlagClientFromSearchParams(searchParams);
    setActiveClient((previousClient) => (
      previousClient === clientFromUrl ? previousClient : clientFromUrl
    ));
  }, [searchParams]);

  const assignmentConfirmationContent = useMemo(() => {
    if (!pendingAssignment) {
      return null;
    }

    if (pendingAssignment.assigned) {
      return {
        confirmButtonText: 'Assign Feature',
        confirmTone: 'primary' as const,
        message: `This will assign ${pendingAssignment.featureName} to this store.`,
        title: 'Assign Feature?',
      };
    }

    return {
      confirmButtonText: 'Remove Assignment',
      confirmTone: 'danger' as const,
      message: `This will remove ${pendingAssignment.featureName} from this store.`,
      title: 'Remove Assignment?',
    };
  }, [pendingAssignment]);

  const loadFeatureFlags = useCallback(async () => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;

    if (!storeId) {
      setRows([]);
      setError('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setMutationError('');

    try {
      const clientApplication = mapStoreDetailsFeatureFlagClientToApiClient(activeClient);
      const response = await storeDetailsFeatureFlagsService.getStoreFeatureFlags(storeId, clientApplication);

      if (requestSequenceRef.current !== requestSequence) {
        return;
      }

      setRows(mapStoreDetailsFeatureFlagsResponseToRows(response));
    } catch (loadError) {
      if (requestSequenceRef.current !== requestSequence) {
        return;
      }

      setRows([]);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load store feature flags');
    } finally {
      if (requestSequenceRef.current === requestSequence) {
        setLoading(false);
      }
    }
  }, [activeClient, storeId]);

  useEffect(() => {
    void loadFeatureFlags();
  }, [loadFeatureFlags]);

  const onClientChange = useCallback((client: StoreDetailsFeatureFlagClient | null) => {
    if (client && !mutatingFeatureId) {
      setPendingAssignment(null);
      setMutationError('');
      setActiveClient(client);
      setSearchParams(buildStoreDetailsFeatureFlagClientSearchParams(searchParams, client), { replace: true });
    }
  }, [mutatingFeatureId, searchParams, setSearchParams]);

  const onAssignmentChange = useCallback((featureId: string, assigned: boolean) => {
    const featureRow = rows.find((row) => row.id === featureId);

    if (
      !featureRow
      || featureRow.rolloutScope !== 'selected'
      || featureRow.rolloutTarget !== 'stores'
      || mutatingFeatureId
    ) {
      return;
    }

    setPendingAssignment({
      assigned,
      featureId,
      featureName: featureRow.displayName,
    });
    setMutationError('');
  }, [mutatingFeatureId, rows]);

  const onAssignmentConfirmationCancel = useCallback(() => {
    if (!mutatingFeatureId) {
      setPendingAssignment(null);
    }
  }, [mutatingFeatureId]);

  const onAssignmentConfirmationConfirm = useCallback(async () => {
    if (!pendingAssignment || mutatingFeatureId) {
      return;
    }

    const featureRow = rows.find((row) => row.id === pendingAssignment.featureId);

    if (!featureRow || featureRow.rolloutScope !== 'selected' || featureRow.rolloutTarget !== 'stores') {
      setPendingAssignment(null);
      return;
    }

    const currentAssignedFeatureIds = rows
      .filter((row) => row.rolloutScope === 'selected' && row.rolloutTarget === 'stores' && row.assignedToStore)
      .map((row) => row.id);
    const nextAssignedFeatureIds = pendingAssignment.assigned
      ? Array.from(new Set([...currentAssignedFeatureIds, pendingAssignment.featureId]))
      : currentAssignedFeatureIds.filter((assignedFeatureId) => assignedFeatureId !== pendingAssignment.featureId);

    setMutatingFeatureId(pendingAssignment.featureId);
    setMutationError('');

    try {
      await storeDetailsFeatureFlagsService.replaceStoreFeatureFlagAssignments(storeId, {
        client_application: mapStoreDetailsFeatureFlagClientToApiClient(activeClient),
        feature_flag_ids: nextAssignedFeatureIds,
      });
      showToast.success(
        pendingAssignment.assigned ? 'Feature assigned to store.' : 'Feature assignment removed.',
      );
      await loadFeatureFlags();
    } catch (assignmentError) {
      setMutationError(
        assignmentError instanceof Error ? assignmentError.message : 'Failed to save store feature assignments',
      );
    } finally {
      setPendingAssignment(null);
      setMutatingFeatureId('');
    }
  }, [activeClient, loadFeatureFlags, mutatingFeatureId, pendingAssignment, rows, storeId]);

  return {
    activeClient,
    assignmentConfirmationContent,
    error,
    loading,
    mutatingFeatureId,
    mutationError,
    onAssignmentChange,
    onAssignmentConfirmationCancel,
    onAssignmentConfirmationConfirm,
    onClientChange,
    onRetry: loadFeatureFlags,
    rows,
  };
};
